import React from 'react';
import DeleteIcon from '@material-ui/icons/Delete';
import {
    Box,
    Button,
    FormControl,
    FormControlLabel,
    FormControlLabelProps,
    FormGroup,
    FormHelperText,
    IconButton,
    InputBase,
    Switch as MuiSwitch,
    SwitchProps as MuiSwitchProps,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { Field, FieldInputProps, Form, Formik } from 'formik';
import { Card, CardContent, Typography } from '../../../../../../ui-kit';
import { placeValidationSchema } from './validation';

export interface EditTableViewFormProps {
    tableId: string;
    sectionId: string;
    tableName: string;
    isToGo: boolean;
    onClose: () => void;
    onRemoveClick: (tableId: string, sectionId: string) => void;
    onSubmit: ({ placeId, name, isToGo }: { placeId: string; name: string; isToGo: boolean }) => void;
}

interface FormValues {
    name: string;
    isToGo: boolean;
}

interface InputProps extends FieldInputProps<FormValues['name']> {
    error?: string;
    touched?: boolean;
}

const Input = (props: InputProps) => {
    const { checked, multiple, error, touched, ...other } = props;

    return (
        <FormControl>
            <InputBase {...other} />
            <FormHelperText error={!!error}>{touched && !!error ? error : ''}</FormHelperText>
        </FormControl>
    );
};

type SwitchProps = MuiSwitchProps & Omit<FormControlLabelProps, 'control'> & FieldInputProps<FormValues['isToGo']>;

const Switch = (props: SwitchProps) => {
    const { label, color, labelPlacement, value, onChange, name } = props;

    return (
        <FormControl>
            <FormControlLabel
                control={(
                    <MuiSwitch
                        name={name}
                        color={color}
                        checked={value}
                        value={value}
                        onChange={onChange}
                    />
                )}
                label={label}
                labelPlacement={labelPlacement}
            />
        </FormControl>
    );
};

const EditTableViewForm = (props: EditTableViewFormProps) => {
    const { tableName, sectionId, onRemoveClick, onSubmit, tableId, isToGo } = props;
    const initialValues = {
        name: tableName,
        isToGo,
    };
    const { t } = useTranslation();
    const handleTableRemove = () => onRemoveClick(tableId, sectionId);
    const handleSubmit = (values: FormValues) => onSubmit({ placeId: tableId,...values });

    return (
        <Card>
            <Box width={208}>
                <CardContent padding={2}>
                    <Formik
                        initialValues={initialValues}
                        validationSchema={placeValidationSchema}
                        onSubmit={handleSubmit}
                    >
                        {formikProps => (
                            <Form>
                                <FormGroup>
                                    <Field
                                        name='name'
                                        as={Input}
                                        error={formikProps.errors.name}
                                        touched={formikProps.touched.name}
                                    />
                                    <Typography uppercase variant='subtitle2'>{t('take away')}</Typography>
                                    <Field
                                        name='isToGo'
                                        label={formikProps.values.isToGo ? t('Yes') : t('No')}
                                        as={Switch}
                                        color='primary'
                                        labelPlacement='end'
                                    />
                                    <Box display='flex' width='100%' justifyContent='space-between' alignItems='center'>
                                        <Box mr={2}>
                                            <IconButton
                                                color="secondary"
                                                onClick={handleTableRemove}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                        <Button
                                            fullWidth
                                            type='submit'
                                            variant='outlined'
                                            color='primary'
                                        >
                                            {t(`Update`)}
                                        </Button>
                                    </Box>
                                </FormGroup>
                            </Form>
                        )}
                    </Formik>
                </CardContent>
            </Box>
        </Card>
    );
};

export default EditTableViewForm;
